import React, {useState, useEffect, useMemo} from 'react';
import styled from 'styled-components';
import {useSelector, useDispatch} from 'react-redux';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import SearchRoundedIcon from '@material-ui/icons/SearchRounded';
import IconButton from '@material-ui/core/IconButton';
import ClearRoundedIcon from '@material-ui/icons/ClearRounded';
import CircularProgress from '@material-ui/core/CircularProgress';
import SearchSuggestionsStoreHealth from './SearchSuggestionsStoreHealth';
import {useHistory} from 'react-router-dom';
import {fetchMinimumStoreInfo, fetchDevicesCatalog} from '../../redux/actions';
import {useOktaAuth} from '@okta/okta-react';
import {
  clearPendingStoreHcDeviceSearch,
  writePendingStoreHcDeviceSearch,
} from '../../utils/storeHcPendingSearch';

const StyledSearch = styled.div`
  /* margin-right: 20px; */
  /* padding-top: 20px; */
  height: 30px;
  margin-bottom: 10px;
  margin-top: 10px;
  position: relative;
  z-index: 11;
  display: block;
`;

const StyledTextField = styled(TextField)`
  .MuiFilledInput-root {
    background-color: ${props => props.theme.barBg};
    color: ${props => props.theme.text};
    border-radius: 10px;
  }
  &.search-active .MuiFilledInput-root {
    border-bottom-left-radius: 0px;
    border-bottom-right-radius: 0px;
  }
  .MuiInputAdornment-filled.MuiInputAdornment-positionStart:not(.MuiInputAdornment-hiddenLabel) {
    margin-top: 1px;
  }
  .MuiFilledInput-input {
    padding: 8px 10px 8px;
    font-size: 0.8rem;
    width: 280px;
  }
  .MuiFilledInput-underline:after,
  .MuiFilledInput-underline:before {
    border-bottom: none;
  }
`;

const StyledIconButton = styled(IconButton)`
  padding: 0px 0px 0px;
  width: 24px;
`;

const StyledSearchIcon = styled(SearchRoundedIcon)`
  color: ${props => props.theme.subtleText};
`;

const StyledClearRoundedIcon = styled(ClearRoundedIcon)`
  color: ${props => props.theme.subtleText};
`;

const StyledCircleLoading = styled(CircularProgress)`
  height: 24px;
  width: 24px;
  svg {
    color: ${props => props.theme.subtleText};
  }
`;

function SearchStoreHealth({region}) {
  const {authState, oktaAuth} = useOktaAuth();
  const dispatch = useDispatch();

  useEffect(() => {
    if (authState && authState.isAuthenticated) {
      const accessToken = oktaAuth.getAccessToken();
      dispatch(fetchMinimumStoreInfo(accessToken));
      dispatch(fetchDevicesCatalog(accessToken));
    }
  }, [oktaAuth, authState, dispatch]);

  let history = useHistory();
  const storeApi = useSelector(state => state.data.app.searchMinimumStoreInfo.data);
  const storeApiReady = useSelector(state => state.data.app.searchMinimumStoreInfo.dataRetrieved);
  const devicesCatalog = useSelector(state => state.data.app.devicesCatalog.data);
  const storeApiFilteredByBrand = useMemo(
    () => storeApi?.filter(store => store.Brand === 'Nike') ?? [],
    [storeApi]
  );
  const storeBySiteId = useMemo(() => {
    const map = new Map();
    storeApiFilteredByBrand.forEach(store => {
      if (store?.SiteId != null) {
        map.set(String(store.SiteId), store);
      }
    });
    return map;
  }, [storeApiFilteredByBrand]);

  const [searchValue, setSearchValue] = useState('');
  const [filteredSearch, setFilteredSearch] = useState([]);

  useEffect(() => {
    if (!storeApiReady || storeApiFilteredByBrand.length === 0 || searchValue.length === 0) {
      clearSearch();
      return;
    }

    const search = searchValue.toLowerCase();
    const searchStr = searchValue.toString();

    const storeNumberMatch = storeApiFilteredByBrand.filter(store => {
      return store.StoreNumber === parseInt(searchValue);
    });

    const otherMatchingStores = storeApiFilteredByBrand.filter(store => {
      const storeName = store?.StoreName;
      return (
        store.StoreNumber.toString().indexOf(searchStr) > -1 ||
        (storeName != null ? String(storeName).toLowerCase().indexOf(search) > -1 : false)
      );
    });

    if (storeNumberMatch.length > 0) {
      const idx = otherMatchingStores.indexOf(storeNumberMatch[0]);
      if (idx !== -1) {
        otherMatchingStores.splice(idx, 1);
      }
    }

    const orderedStores = [...storeNumberMatch, ...otherMatchingStores];
    const storeResults = orderedStores.map(store => ({kind: 'store', store}));

    let deviceResults = [];
    if (Array.isArray(devicesCatalog) && devicesCatalog.length > 0) {
      const seen = new Set();
      deviceResults = devicesCatalog
        .filter(device => {
          const serial = device?.serialNumber ? String(device.serialNumber).toLowerCase() : '';
          const name = device?.deviceName ? String(device.deviceName).toLowerCase() : '';
          if (!serial && !name) return false;
          return serial.indexOf(search) > -1 || name.indexOf(search) > -1;
        })
        .map(device => {
          const store = storeBySiteId.get(String(device.siteId));
          return store ? {kind: 'device', store, device} : null;
        })
        .filter(item => {
          if (!item) return false;
          const dedupeKey = `${item.store.SiteId}|${item.device.serialNumber}|${item.device.deviceName}`;
          if (seen.has(dedupeKey)) return false;
          seen.add(dedupeKey);
          return true;
        });
    }

    setFilteredSearch([...storeResults, ...deviceResults]);
  }, [storeApiReady, storeApiFilteredByBrand, devicesCatalog, storeBySiteId, searchValue]);

  const handleChange = event => {
    setSearchValue(event.target.value);
  };

  const navigateToResult = result => {
    const selectedRegion = sessionStorage.getItem('storeHealth:selectedRegion');
    if (result.kind === 'device') {
      writePendingStoreHcDeviceSearch({
        siteId: String(result.store.SiteId),
        selectedRegion,
        searchedDevice: {
          deviceType: result.device.deviceType,
          serialNumber: result.device.serialNumber,
          deviceName: result.device.deviceName,
        },
      });
      history.push({
        pathname: `/store/${result.store.SiteId}/retail-hardware-status`,
        state: {
          selectedRegion,
          searchedDevice: {
            deviceType: result.device.deviceType,
            serialNumber: result.device.serialNumber,
            deviceName: result.device.deviceName,
          },
        },
      });
      return;
    }
    clearPendingStoreHcDeviceSearch();
    history.push({
      pathname: `/store/${result.store.SiteId}/retail-hardware-status`,
      state: {selectedRegion},
    });
  };

  const enterPressed = event => {
    let code = event.keyCode || event.which;
    const keyCode = 13; //13 is the enter keycode
    if (code === keyCode) {
      if (filteredSearch.length > 0) {
        navigateToResult(filteredSearch[0]);
        clearSearch();
        clearUserInput();
      }
    }
  };

  const clearSearch = () => {
    setFilteredSearch([]);
  };

  const clearUserInput = () => {
    setSearchValue('');
  };

  return (
    <StyledSearch>
      <StyledTextField
        id="search-field"
        variant="filled"
        className={filteredSearch.length > 0 ? 'search-active' : ''}
        onChange={handleChange}
        placeholder={'Store, Device or Serial #'}
        autoComplete={'off'}
        value={searchValue}
        onKeyDown={enterPressed}
        inputProps={{
          'aria-label': 'Search by store name, store number, device name, or serial number',
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {storeApiReady ? <StyledSearchIcon /> : <StyledCircleLoading size={24} />}
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <StyledIconButton aria-label="clear search field" onClick={clearUserInput}>
                {searchValue.length > 0 ? <StyledClearRoundedIcon /> : ''}
              </StyledIconButton>
            </InputAdornment>
          ),
        }}
      />
      <SearchSuggestionsStoreHealth results={filteredSearch} clearSearch={clearSearch} />
    </StyledSearch>
  );
}

export default SearchStoreHealth;
